import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CountBox from "./CountBox";
import CommonChip from "../../commonComponents/commonChip/CommonChip";
import { useFormik } from "formik";
import {
  welcomeValidationSchema,
  initialFormValues,
} from "./welcomeValidation";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import { maskPhoneNumber } from "../../commonComponents/helperFunctions/HelperFunction.js";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import PhoneInput from "react-phone-input-2";
import arrowImg from "../../../assets/images/arrow-white-icon.svg";
import requestOtpImg from "../../../assets/images/request-otp.svg";
import uploadImg from "../../../assets/images/upload.svg";

import authService from "../../../services/authService";
import { apiService, apiClient } from "../../../services/apiService";
import Header from "../../commonComponents/header/Header";
import Footer from "../../commonComponents/footer/Footer";
import DraggableChip from "./DragableChip";
import { calculateAge } from "../../../utils/dateUtils";
import removeImg from "../../../assets/images/remove-icon.svg";
import removeWhiteImg from "../../../assets/images/remove-icon.svg";
import rightImg from "../../../assets/images/right-white-icon.svg";
import LoadingDialog from "../../commonComponents/loader/LoadingDialog.js";
import "./welcome.css";
import formatText from "../../commonComponents/formatText/FormatText.js";
import { decryptToken } from "../../../utils/decryptURL";
import axios from "axios";

import { Country, State } from "country-state-city";
import { Turnstile } from "@marsidev/react-turnstile";
import removeDarkImg from "../../../assets/images/remove-dark-icon.svg";
import CustomDatePicker from "../../commonComponents/CustomDatePicker.js";

// ─── Custom Date Picker Component ─────────────────────────────────────────────
const DatePicker = ({ value, onChange, onBlur, name, max }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    if (value) return new Date(value);
    return new Date();
  });
  const pickerRef = useRef(null);

  const today = max ? new Date(max) : new Date();
  const selectedDate = value ? new Date(value + "T00:00:00") : null;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setViewMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setViewMonth(new Date(year, month + 1, 1));

  const handleDayClick = (day) => {
    const d = new Date(year, month, day);
    if (d > today) return;
    const formatted = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    onChange({ target: { name, value: formatted } });
    setIsOpen(false);
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return (
      selectedDate.getFullYear() === year &&
      selectedDate.getMonth() === month &&
      selectedDate.getDate() === day
    );
  };

  const isToday = (day) => {
    const t = new Date();
    return (
      t.getFullYear() === year && t.getMonth() === month && t.getDate() === day
    );
  };

  const isFuture = (day) => new Date(year, month, day) > today;

  const displayValue = selectedDate
    ? `${String(selectedDate.getDate()).padStart(2, "0")} ${monthNames[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`
    : "";

  const yearRange = Array.from(
    { length: 101 },
    (_, i) => today.getFullYear() - i,
  );

  return (
    <div className="relative w-full max-w-[450px]" ref={pickerRef}>
      <div
        className={`flex items-center justify-between bg-[#F9FAFB] border rounded-[4px] px-2 py-[3.5px] cursor-pointer select-none ${
          isOpen ? "border-[#666666]" : "border-[#666666]"
        }`}
        style={{ borderWidth: "0.25px" }}
        onClick={() => setIsOpen((p) => !p)}
        onBlur={onBlur}
      >
        <span
          className={`text-allfonts ${displayValue ? "text-fontColor" : "text-gray-400"}`}
        >
          {displayValue || "DD MMM YYYY"}
        </span>
        <svg
          className="w-4 h-4 ml-2 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" strokeWidth="1.5" />
          <path
            d="M16 2v4M8 2v4M3 10h18"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 bg-white rounded-[4px] shadow-2xl z-[10] overflow-hidden"
          style={{ width: "280px", border: "0.5px solid #e5e7eb" }}
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[#ffffff]">
            <button
              type="button"
              onClick={prevMonth}
              className="text-[#111729] transition-colors p-1 rounded"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div className="flex items-center gap-2">
              <select
                value={month}
                onChange={(e) =>
                  setViewMonth(new Date(year, parseInt(e.target.value), 1))
                }
                className="bg-transparent text-[#111729] text-sm font-medium focus:outline-none cursor-pointer"
                style={{ appearance: "none" }}
              >
                {monthNames.map((m, i) => (
                  <option key={m} value={i} className="text-black bg-white">
                    {m}
                  </option>
                ))}
              </select>
              <select
                value={year}
                onChange={(e) =>
                  setViewMonth(new Date(parseInt(e.target.value), month, 1))
                }
                className="bg-transparent text-[#111729] text-sm font-medium focus:outline-none cursor-pointer"
                style={{ appearance: "none" }}
              >
                {yearRange.map((y) => (
                  <option key={y} value={y} className="text-black bg-white">
                    {y}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={nextMonth}
              className="text-[#111729] transition-colors p-1 rounded"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 px-3 pt-3 pb-1">
            {dayNames.map((d) => (
              <div
                key={d}
                className="text-center text-xs font-semibold text-gray-400 py-1"
              >
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 px-3 pb-3 gap-y-1">
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => (
              <button
                key={day}
                type="button"
                disabled={isFuture(day)}
                onClick={() => handleDayClick(day)}
                className={`
                  w-8 h-8 mx-auto flex items-center justify-center text-xs rounded-full
                  transition-all duration-150 font-medium
                  ${isFuture(day) ? "text-gray-300 cursor-not-allowed" : "cursor-pointer hover:bg-[#111729] hover:text-white"}
                  ${isSelected(day) ? "bg-[#111729] text-white shadow-md" : ""}
                  ${isToday(day) && !isSelected(day) ? "border text-[#111729] font-bold" : ""}
                `}
                style={
                  isToday(day) && !isSelected(day)
                    ? { borderWidth: "0.5px", borderColor: "#111729" }
                    : {}
                }
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Constants ────────────────────────────────────────────────────────────────
const ethnicityOptions = [
  { id: 1, name: "Hispanic or Latino" },
  { id: 2, name: "White" },
  { id: 3, name: "Black or African American" },
  { id: 4, name: "Native American or Alaska Native" },
  { id: 5, name: "Asian" },
  { id: 6, name: "Native Hawaiian or Pacific Islander" },
  { id: 7, name: "Middle Eastern or North African" },
  { id: 8, name: "Mixed or Multi-Racial" },
  { id: 9, name: "Prefer not to say" },
  { id: 10, name: "South Asian" },
  { id: 11, name: "East Asian" },
  { id: 12, name: "Southeast Asian" },
  { id: 13, name: "Caribbean" },
  { id: 14, name: "Sub-Saharan African" },
  { id: 15, name: "Central Asian" },
  { id: 16, name: "Indigenous peoples of the Americas" },
];

// const currenciesOption = [
//   { code: "USD", name: "United States Dollar", symbol: "$" },
//   { code: "INR", name: "Indian Rupee", symbol: "₹" },
//   { code: "EUR", name: "Euro", symbol: "€" },
//   { code: "GBP", name: "British Pound Sterling", symbol: "£" },
//   { code: "JPY", name: "Japanese Yen", symbol: "¥" },
//   { code: "AUD", name: "Australian Dollar", symbol: "A$" },
//   { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
//   { code: "CHF", name: "Swiss Franc", symbol: "Fr" },
//   { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
//   { code: "SEK", name: "Swedish Krona", symbol: "kr" },
//   { code: "NZD", name: "New Zealand Dollar", symbol: "NZ$" },
//   { code: "MXN", name: "Mexican Peso", symbol: "$" },
//   { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
//   { code: "HKD", name: "Hong Kong Dollar", symbol: "HK$" },
//   { code: "KRW", name: "South Korean Won", symbol: "₩" },
//   { code: "TRY", name: "Turkish Lira", symbol: "₺" },
//   { code: "RUB", name: "Russian Ruble", symbol: "₽" },
//   { code: "BRL", name: "Brazilian Real", symbol: "R$" },
//   { code: "ZAR", name: "South African Rand", symbol: "R" },
//   { code: "PLN", name: "Polish Zloty", symbol: "zł" },
//   { code: "IDR", name: "Indonesian Rupiah", symbol: "Rp" },
//   { code: "THB", name: "Thai Baht", symbol: "฿" },
//   { code: "MYR", name: "Malaysian Ringgit", symbol: "RM" },
//   { code: "VND", name: "Vietnamese Dong", symbol: "₫" },
//   { code: "EGP", name: "Egyptian Pound", symbol: "£" },
//   { code: "PKR", name: "Pakistani Rupee", symbol: "₨" },
//   { code: "NGN", name: "Nigerian Naira", symbol: "₦" },
//   { code: "COP", name: "Colombian Peso", symbol: "$" },
//   { code: "SYP", name: "Syrian Pound", symbol: "£" },
//   { code: "IRR", name: "Iranian Rial", symbol: "﷼" },
//   { code: "BDT", name: "Bangladeshi Taka", symbol: "৳" },
// ];
const currenciesOption = [
  { code: "USD", name: "United States Dollar", symbol: "$" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound Sterling", symbol: "£" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
  { code: "CHF",name: "CHF - Swiss Franc",  symbol: "Fr" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
];

// ─────────────────────────────────────────────
// Country / state lists via country-state-city
// ─────────────────────────────────────────────
const ALL_COUNTRIES = Country.getAllCountries().map((c) => c.name);
const getStatesForCountryCode = (isoCode) =>
  State.getStatesOfCountry(isoCode).map((s) => s.name);

const FIELD_OPTIONS = {
  countryList: ALL_COUNTRIES,
  indianStateList: getStatesForCountryCode("IN"),
  usStateList: getStatesForCountryCode("US"),
  canadianStateList: getStatesForCountryCode("CA"),
  australianStateList: getStatesForCountryCode("AU"),
};

const LIST_TYPES = [
  "countryList",
  "indianStateList",
  "usStateList",
  "canadianStateList",
  "australianStateList",
];

// ─────────────────────────────────────────────
// Map JSON data_type → internal field type
// ─────────────────────────────────────────────
const DATA_TYPE_MAP = {
  number: "number",
  single_date: "singleDate",
  multiple_option_single_selection: "multipleOptionsSingleSelection",
  multiple_option_multi_selection: "multipleOptionsMultipleSelections",
  text_multiple_entries: "textOnlyMultipleEntries",
  currency: "currency",
  boolean: "checkbox",
  list_of_values_text: "listOfValuesText",
  list_of_values_numbers: "listOfValuesNumbers",
  text: "textarea",
  phone: "phone",
  date_range: "dateRange",
  multiple_dates: "multipleDates",
  multiple_options_multiple_selections: "multipleOptionsMultipleSelections",
  sortable_chip_input: "sortableChipInput",
  countries_list: "countryList",
  country_list: "countryList",
  us_states: "usStateList",
  us_state_list: "usStateList",
  indian_states: "indianStateList",
  indian_state_list: "indianStateList",
  canadian_states: "canadianStateList",
  canadian_state_list: "canadianStateList",
  austrailian_states: "australianStateList",
  australian_states: "australianStateList",
  australian_state_list: "australianStateList",
};

function getSemanticId(q) {
  const label = (q.label || "").toLowerCase();
  const dtype = (q.data_type || "").toLowerCase();

  if (dtype === "single_date" && label.includes("birth")) return "dob";
  if (dtype === "multiple_option_single_selection" && label.includes("gender"))
    return "gender";
  if (dtype === "text_multiple_entries" && label.includes("language"))
    return "language";
  if (dtype === "number" && label.includes("experience")) return "experience";
  if (dtype === "currency" && label.includes("current")) return "currentCTC";
  if (dtype === "currency" && label.includes("expected")) return "expectedCTC";
  if (dtype === "boolean" && (label.includes("home") || label.includes("wfh")))
    return "isWFH";
  if (dtype === "text_multiple_entries" && label.includes("location"))
    return "preferred_locations";
  if (
    (dtype === "list_of_values_text" || dtype === "dropdown") &&
    label.includes("ethnicity")
  )
    return "ethnicity";
  if (label.includes("referred")) return "referred";
  if (label.includes("related")) return "related";
  // Fall back to a stable key for any new/custom fields
  return `field_${q.id}`;
}

function normalizeField(q) {
  const rawType = (q.data_type || "").toLowerCase();
  const semanticId = getSemanticId(q);

  let internalType =
    rawType === "text_multiple_entries" && semanticId === "preferred_locations"
      ? "sortableChipInput"
      : DATA_TYPE_MAP[rawType] || rawType;

  const configArray = q.options?.config || [];
  const flatOptions = configArray.length
    ? configArray.map((o) =>
        typeof o === "object" && o !== null ? o.label : String(o),
      )
    : [];

  const defaultOption = Array.isArray(configArray)
    ? configArray.find((o) => o?.default === true)
    : null;

  return {
    id: semanticId,
    numericId: q.id,
    label: q.label,
    description: q.description || null,
    placeholder: q.placeholder || null,
    suffix: q.suffix || null,
    prefix: q.prefix || null,
    type: internalType,
    order: q.display_order,
    required: q.is_required ?? false,
    options: flatOptions,
    defaultValue: defaultOption?.label ?? null,
    min: q.options?.validation?.min ?? null,
    max: q.options?.validation?.max ?? null,
    default_id: q.default_id,
    template_id: q.template_id,
  };
}

function loadNormalizedFieldsFromSession() {
  try {
    const raw = sessionStorage.getItem("screenup_data_collection_template");
    if (!raw) return [];
    const template = JSON.parse(raw);
    return (template.questions || template.fields || []).map(normalizeField);
  } catch {
    return [];
  }
}

function buildInitialValues(fields) {
  const values = {};
  fields.forEach((field) => {
    switch (field.type) {
      case "multiChipInput":
      case "sortableChipInput":
      case "textOnlyMultipleEntries":
      case "multipleOptionsMultipleSelections":
      case "listOfValuesNumbers":
      case "multipleDates":
        values[field.id] = [];
        break;
      case "checkbox":
        values[field.id] = false;
        break;
      case "currency":
        values[field.id] = { amount: "", currencyCode: "INR" };
        break;
      case "dateRange":
        values[field.id] = { from: "", to: "" };
        break;
      default:
        values[field.id] = field.defaultValue ?? "";
    }
  });
  return values;
}

function validateDynamicField(field, value) {
  if (!field.required && field.min == null && field.max == null) return null;
  switch (field.type) {
    case "multiChipInput":
    case "sortableChipInput":
    case "textOnlyMultipleEntries":
    case "multipleOptionsMultipleSelections":
    case "listOfValuesNumbers":
    case "multipleDates":
      if (field.required && Array.isArray(value) && value.length === 0)
        return `${field.label} is required.`;
      return null;
    case "checkbox":
      return null;
    // return value === undefined || value === null|| value===""
    //   ? `${field.label} must be checked.`
    //   : null;
    case "currency":
      if (field.required && !value?.amount) return `${field.label} is required.`;
      if (value?.amount && !value?.currencyCode)
        return `Please select currency for ${field.label}.`;
      return null;
    case "dateRange":
      return !value?.from || !value?.to
        ? `${field.label} requires both From and To dates.`
        : null;
    case "date":
    case "singleDate":
      return field.required && (!value || value.trim() === "")
        ? `${field.label} is required.`
        : null;
    case "number":
      if (field.required && (!value || value.toString().trim() === ""))
        return `${field.label} is required.`;
      const num = parseFloat(value);
      if (!isNaN(num)) {
        if (field.min != null && num < field.min)
          return `${field.label} must be at least ${field.min}.`;
        if (field.max != null && num > field.max)
          return `${field.label} must be at most ${field.max}.`;
      }
      return null;
    default:
      return !value || value.toString().trim() === ""
        ? `${field.label} is required.`
        : null;
  }
}

// ─── Pure utility functions ───────────────────────────────────────────────────
const formatNumberWithCommas = (value) => {
  if (!value || value === "") return "";
  const numericValue = value.toString().replace(/\D/g, "");
  if (!numericValue) return "";
  const lastThree = numericValue.slice(-3);
  const otherDigits = numericValue.slice(0, -3);
  const formattedOther = otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  return otherDigits ? `${formattedOther},${lastThree}` : lastThree;
};

const getNumericValue = (value) =>
  value ? value.toString().replace(/,/g, "") : "";

const isValidPhoneNumber = (phone) => {
  const cleanPhone = phone?.replace(/\D/g, "") || "";
  return cleanPhone.length >= 10;
};

// ─── Custom hooks ─────────────────────────────────────────────────────────────
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// ─── Extracted static sub-components ─────────────────────────────────────────
const SectionLabel = ({ label, required }) => (
  <p className="text-allfonts font-semibold tracking-wide text-fontColor mb-1">
    {label}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </p>
);

const Snackbar = ({ messages, onClose, duration = 5000, type = "error" }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (messages?.length > 0) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(onClose, 300);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [messages, onClose, duration]);

  if (!messages?.length) return null;

  const bgColor = type === "success" ? "bg-green-600" : "bg-red-600";

  return (
    <div
      className={`fixed top-4 left-1/2 -translate-x-1/2 ${bgColor} text-white px-4 sm:px-6 py-3 rounded-lg shadow-lg z-[9999] w-[90%] max-w-md transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
      }`}
    >
      <div className="flex items-center justify-between">
        {messages.length === 1 ? (
          <p className="text-primaryFontSize">{messages[0]}</p>
        ) : (
          <div>
            <p className="text-primaryFontSize font-medium">
              {type === "error"
                ? "Please fix the following errors:"
                : "Success"}
            </p>
            <ul className="text-primaryFontSize space-y-1">
              {messages.map((msg, i) => (
                <li key={i}>• {msg}</li>
              ))}
            </ul>
          </div>
        )}
        <button
          onClick={() => {
            setVisible(false);
            setTimeout(onClose, 300);
          }}
          className="ml-4 text-white hover:text-gray-200"
        >
          <img src={removeWhiteImg} alt="remove" className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

const OTPInput = ({
  length = 6,
  value,
  onChange,
  onSubmit,
  disabled = false,
}) => {
  const [otp, setOtp] = useState(new Array(length).fill(""));
  const [activeOTPIndex, setActiveOTPIndex] = useState(0);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  useEffect(() => {
    if (value) {
      const otpArray = value.split("").slice(0, length);
      setOtp([...otpArray, ...new Array(length - otpArray.length).fill("")]);
    }
  }, [value, length]);

  useEffect(() => {
    inputRefs.current[activeOTPIndex]?.focus();
  }, [activeOTPIndex]);

  const handleOnChange = (e, index) => {
    const { value: v } = e.target;
    if (isNaN(v) || disabled) return;
    const newOtp = [...otp];
    newOtp[index] = v.slice(-1);
    setOtp(newOtp);
    onChange(newOtp.join(""));
    if (v && index < length - 1) setActiveOTPIndex(index + 1);
  };

  const handleOnKeyDown = (e, index) => {
    if (disabled) return;
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (otp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
        onChange(newOtp.join(""));
      } else if (index > 0) {
        newOtp[index - 1] = "";
        setOtp(newOtp);
        onChange(newOtp.join(""));
        setActiveOTPIndex(index - 1);
      }
    } else if (e.key === "Delete") {
      e.preventDefault();
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
      onChange(newOtp.join(""));
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      setActiveOTPIndex(index - 1);
    } else if (e.key === "ArrowRight" && index < length - 1) {
      e.preventDefault();
      setActiveOTPIndex(index + 1);
    } else if (e.key === "Enter") {
      onSubmit();
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;
    e.preventDefault();
    const paste = e.clipboardData.getData("text");
    const pasteArray = paste.replace(/\D/g, "").split("").slice(0, length);
    const newOtp = [...new Array(length).fill("")];
    pasteArray.forEach((char, i) => {
      newOtp[i] = char;
    });
    setOtp(newOtp);
    onChange(newOtp.join(""));
    setActiveOTPIndex(Math.min(pasteArray.length, length - 1));
  };

  return (
    <div className="flex gap-1.5 sm:gap-2">
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          value={digit}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          onChange={(e) => handleOnChange(e, index)}
          onKeyDown={(e) => handleOnKeyDown(e, index)}
          onPaste={handlePaste}
          onFocus={() => setActiveOTPIndex(index)}
          onClick={() => setActiveOTPIndex(index)}
          disabled={disabled}
          className={`w-8 h-8 sm:w-11 sm:h-11 text-center text-sm sm:text-lg border-2 rounded-md focus:outline-none ${
            disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"
          } border-[#666666]`}
          maxLength={1}
          style={{ borderWidth: "0.25px" }}
        />
      ))}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
export default function PublicWelcome() {
  const navigate = useNavigate();
  const { hash } = useParams(); // Get hash from URL path parameter

  // ── Company data ──
  const [data, setData] = useState({});

  // ── Email OTP states ──
  const [emailStep, setEmailStep] = useState("email"); // "email" | "emailOtp" | "emailVerified"
  const [emailValue, setEmailValue] = useState("");
  const [emailOtpValue, setEmailOtpValue] = useState("");
  const [isEmailOtpSending, setIsEmailOtpSending] = useState(false);
  const [isEmailOtpVerifying, setIsEmailOtpVerifying] = useState(false);
  const [emailResendTimer, setEmailResendTimer] = useState(0);
  const emailResendIntervalRef = useRef(null);

  // ── Phone OTP states ──
  const [otpStep, setOtpStep] = useState("phone"); // "phone" | "otp" | "verified"
  const [otpValue, setOtpValue] = useState("");
  const [isOtpSending, setIsOtpSending] = useState(false);
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);
  const [userId, setUserId] = useState(null);
  const [phoneResendTimer, setPhoneResendTimer] = useState(0);
  const phoneResendIntervalRef = useRef(null);
  const phoneInputWrapperRef = useRef(null);

  // ── Phone input dropdown: reposition using fixed coords so it escapes overflow clipping ──
  useEffect(() => {
    const wrapper = phoneInputWrapperRef.current;
    if (!wrapper) return;

    const repositionDropdown = () => {
      const btn = wrapper.querySelector(".flag-dropdown");
      const list = wrapper.querySelector(".country-list");
      if (!btn || !list) return;
      requestAnimationFrame(() => {
        const rect = btn.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const dropdownHeight = Math.min(200, list.scrollHeight || 200);
        const spaceBelow = viewportHeight - rect.bottom;
        const openDown = spaceBelow >= dropdownHeight || spaceBelow >= rect.top;
        list.style.left = rect.left + "px";
        list.style.width = Math.max(rect.width, 260) + "px";
        list.style.bottom = "auto";
        list.style.top = openDown
          ? rect.bottom + 2 + "px"
          : Math.max(8, rect.top - dropdownHeight - 2) + "px";
      });
    };

    // Keep dropdown anchored while the user scrolls or resizes
    const handleScrollOrResize = () => {
      const list = wrapper.querySelector(".country-list");
      if (list) repositionDropdown();
    };

    const observer = new MutationObserver(() => {
      const list = wrapper.querySelector(".country-list");
      if (list) {
        repositionDropdown();
        // Attach listeners when dropdown opens, remove when it closes
        window.addEventListener("scroll", handleScrollOrResize, true);
        window.addEventListener("resize", handleScrollOrResize);
      } else {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      }
    });

    observer.observe(wrapper, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, []);

  // ── Modal/loader states ──
  const [showPhoneConfirmation, setShowPhoneConfirmation] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingWfh, setPendingWfh] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  // ── Resume upload ──
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeError, setResumeError] = useState("");

  // ── URL / company params ──
  const [urlParams, setUrlParams] = useState({ jobDescriptionId: null });
  const [companyId, setCompanyId] = useState(null);

  // ── Snackbar ──
  const [snackbarMessages, setSnackbarMessages] = useState([]);
  const [snackbarType, setSnackbarType] = useState("error");

  // ── Dynamic form state (JSON-driven fields) ──
  const [normalizedFields, setNormalizedFields] = useState(
    loadNormalizedFieldsFromSession,
  );

  const sortedFields = useMemo(
    () => [...normalizedFields].sort((a, b) => a.order - b.order),
    [normalizedFields],
  );

  const [formValues, setFormValues] = useState(() =>
    buildInitialValues(normalizedFields),
  );
  const [inputValues, setInputValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [currencyMasterFieldId, setCurrencyMasterFieldId] = useState(null);

  const updateFieldValue = useCallback((fieldId, value) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
    setFieldErrors((prev) => ({ ...prev, [fieldId]: null }));
  }, []);

  const handleCurrencyChange = useCallback(
    (sourceFieldId, newCurrencyCode) => {
      setCurrencyMasterFieldId(sourceFieldId);
      setFormValues((prev) => {
        const next = { ...prev };
        sortedFields.forEach((f) => {
          if (f.type === "currency") {
            const currentVal =
              next[f.id] && typeof next[f.id] === "object"
                ? next[f.id]
                : { amount: "" };
            next[f.id] = {
              ...currentVal,
              currencyCode: newCurrencyCode,
            };
          }
        });
        return next;
      });
      // Clear errors on currency fields if present
      setFieldErrors((prev) => {
        const nextErr = { ...prev };
        sortedFields.forEach((f) => {
          if (f.type === "currency" && nextErr[f.id]) {
            nextErr[f.id] = null;
          }
        });
        return nextErr;
      });
    },
    [sortedFields],
  );

  // ── Captcha (Turnstile) ──
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [consentGiven, setConsentGiven] = useState(false);
  const [captchaMessage, setCaptchaMessage] = useState(
    "Please complete the verification below.",
  );

  // ── Field-level state ──
  const [language, setLanguage] = useState("");
  const [location, setLocation] = useState("");
  const [isExperience, setIsExperience] = useState(false);
  const [isLocationPreference, setIsLocationPreference] = useState(false);
  const [isWFH, setIsWFH] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // ── Display (formatted) values ──
  const [experienceDisplay, setExperienceDisplay] = useState("");
  const [currentCTCDisplay, setCurrentCTCDisplay] = useState("");
  const [expectedCTCDisplay, setExpectedCTCDisplay] = useState("");

  // ── Language typeahead ──
  const [languageSuggestions, setLanguageSuggestions] = useState([]);
  const [showLanguageSuggestions, setShowLanguageSuggestions] = useState(false);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // ── Location typeahead ──
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [selectedLocationSuggestionIndex, setSelectedLocationSuggestionIndex] =
    useState(-1);

  const debouncedLanguage = useDebounce(language, 300);

  // ── Decrypt URL params & set companyId ──
  useEffect(() => {
    const fetchPublicJobDescription = async () => {
      try {
        // Get hash from URL path parameter
        const publicHash = hash;

        if (!publicHash) {
          console.error("Public hash not found");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_PUBLIC_JD_URL}public_get-job-description/`,
          {
            params: {
              public_hash: publicHash,
            },
            headers: {
              accept: "application/json",
            },
          },
        );
        const data = response.data;

        setData(data);

        const jobDescriptionId = data.id;
        const companyId = data.company_id;

        sessionStorage.setItem("jdId", jobDescriptionId);
        sessionStorage.setItem("companyId", companyId);
        sessionStorage.setItem("companyName", data.company_name || "");
        sessionStorage.setItem("role", data.role || "");

        setUrlParams({
          jobDescriptionId,
        });

        setCompanyId(companyId);

        // Optional - if you need company name later
        sessionStorage.setItem("companyName", data.company_name || "");

        console.log("Public Hash:", publicHash);
        console.log("Job Description Id:", jobDescriptionId);
        console.log("Company Id:", companyId);
      } catch (err) {
        console.error("Failed to fetch public job description:", err);
      }
    };

    fetchPublicJobDescription();
  }, [hash]);

  // ── Snackbar helpers ──
  const showSnackbar = useCallback((messages, type = "error") => {
    setSnackbarMessages(Array.isArray(messages) ? messages : [messages]);
    setSnackbarType(type);
  }, []);

  const handleCloseSnackbar = useCallback(() => setSnackbarMessages([]), []);

  const FIELD_LABELS = useMemo(
    () => ({
      phoneNumber: "Phone number",
      age: "Age",
      gender: "Gender",
      language: "Languages",
      experience: "Experience",
      currencyCode: "Currency",
      currentCTC: "Current CTC",
      expectedCTC: "Expected CTC",
      location: "Location",
      ethnicity: "Ethnicity",
    }),
    [],
  );

  const showErrorsInSnackbar = useCallback(
    (errors) => {
      const msgs = Object.keys(errors)
        .filter((f) => errors[f])
        .map((f) => errors[f]);
      if (msgs.length) showSnackbar(msgs, "error");
    },
    [FIELD_LABELS, showSnackbar],
  );

  // ── Formik ──
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema: welcomeValidationSchema,
    onSubmit: async () => {},
  });

  // ── Sync experience display ──
  useEffect(() => {
    if (formValues["experience"] && !isExperience) {
      setExperienceDisplay(formatNumberWithCommas(formValues["experience"]));
    }
  }, [formValues, isExperience]);

  // ── Email OTP helpers ──
  const startEmailResendTimer = useCallback(() => {
    setEmailResendTimer(30);
    if (emailResendIntervalRef.current)
      clearInterval(emailResendIntervalRef.current);
    emailResendIntervalRef.current = setInterval(() => {
      setEmailResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(emailResendIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSendEmailOtp = useCallback(async () => {
    if (!emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
      showSnackbar("Please enter a valid email address", "error");
      return;
    }
    setIsEmailOtpSending(true);
    try {
      await apiService.sendEmailOtpV2(emailValue);
      showSnackbar("OTP sent successfully to your email", "success");
      setEmailStep("emailOtp");
      startEmailResendTimer();
    } catch (error) {
      showSnackbar(error.message || "Failed to send email OTP", "error");
    } finally {
      setIsEmailOtpSending(false);
    }
  }, [emailValue, showSnackbar, startEmailResendTimer]);

  const handleResendEmailOtp = useCallback(async () => {
    if (emailResendTimer > 0) return;
    setIsEmailOtpSending(true);
    try {
      await apiService.sendEmailOtpV2(emailValue);
      showSnackbar("OTP resent to your email", "success");
      startEmailResendTimer();
    } catch (error) {
      showSnackbar(error.message || "Failed to resend email OTP", "error");
    } finally {
      setIsEmailOtpSending(false);
    }
  }, [emailResendTimer, emailValue, showSnackbar, startEmailResendTimer]);

  const handleVerifyEmailOtp = useCallback(async () => {
    if (!emailOtpValue || emailOtpValue.length !== 6) {
      showSnackbar("Please enter a valid 6-digit OTP", "error");
      return;
    }
    setIsEmailOtpVerifying(true);
    try {
      await apiService.verifyEmailOtpV2(emailValue, emailOtpValue);
      showSnackbar("Email verified successfully!", "success");
      setEmailStep("emailVerified");
    } catch (error) {
      showSnackbar(error.message || "OTP verification failed", "error");
    } finally {
      setIsEmailOtpVerifying(false);
    }
  }, [emailOtpValue, emailValue, showSnackbar]);

  // ── Phone OTP helpers ──
  const startPhoneResendTimer = useCallback(() => {
    setPhoneResendTimer(30);
    if (phoneResendIntervalRef.current)
      clearInterval(phoneResendIntervalRef.current);
    phoneResendIntervalRef.current = setInterval(() => {
      setPhoneResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(phoneResendIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSendOtpClick = useCallback(() => {
    if (
      !formik.values.phoneNumber ||
      !isValidPhoneNumber(formik.values.phoneNumber)
    ) {
      showSnackbar("Please enter a valid phone number", "error");
      return;
    }
    setShowPhoneConfirmation(true);
  }, [formik.values.phoneNumber, showSnackbar]);

  const handleSendOtp = useCallback(async () => {
    setShowPhoneConfirmation(false);
    setIsOtpSending(true);
    try {
      await apiService.sendPhoneOtpV2(formik.values.phoneNumber, companyId);
      showSnackbar("OTP sent successfully to your phone number", "success");
      setOtpStep("otp");
      startPhoneResendTimer();
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setIsOtpSending(false);
    }
  }, [
    formik.values.phoneNumber,
    companyId,
    showSnackbar,
    startPhoneResendTimer,
  ]);

  const handleVerifyOtp = useCallback(async () => {
    setIsOtpVerifying(true);
    if (!otpValue || otpValue.length !== 6) {
      showSnackbar("Please enter a valid 6-digit OTP", "error");
      setIsOtpVerifying(false);
      return;
    }
    try {
      await apiService.verifyPhoneOtpV2(
        formik.values.phoneNumber,
        otpValue,
        companyId,
      );
      showSnackbar("Phone number verified successfully!", "success");
      setOtpStep("verified");
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setIsOtpVerifying(false);
    }
  }, [otpValue, formik.values.phoneNumber, companyId, showSnackbar]);

  // ── Language handlers ──
  const fetchLanguageSuggestions = useCallback(
    async (query) => {
      if (!query.trim()) {
        setLanguageSuggestions([]);
        setShowLanguageSuggestions(false);
        return;
      }
      setIsLoadingLanguages(true);
      try {
        const token = sessionStorage.getItem("authToken");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL_NEW}/languages/search`,
          {
            params: { q: query, limit: 10, company_id: companyId },
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const suggestions = response.data.results || response.data || [];
        setLanguageSuggestions(suggestions);
        setShowLanguageSuggestions(suggestions.length > 0);
      } catch (error) {
        console.error("Error fetching language suggestions:", error);
        setLanguageSuggestions([]);
        setShowLanguageSuggestions(false);
      } finally {
        setIsLoadingLanguages(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    if (otpStep === "verified") fetchLanguageSuggestions(debouncedLanguage);
  }, [debouncedLanguage, otpStep, fetchLanguageSuggestions]);

  const handleInputChange = useCallback((e) => {
    setLanguage(formatText(e.target.value));
    setSelectedSuggestionIndex(-1);
  }, []);

  const handleSuggestionClick = useCallback(
    (suggestion) => {
      const current = formValues["language"] || [];
      if (!current.includes(suggestion)) {
        updateFieldValue("language", [...current, suggestion]);
      }
      setLanguage("");
      setShowLanguageSuggestions(false);
      setSelectedSuggestionIndex(-1);
    },
    [formValues, updateFieldValue],
  );

  const handleRemoveLanguage = useCallback(
    (lang) => {
      updateFieldValue(
        "language",
        (formValues["language"] || []).filter((l) => l !== lang),
      );
    },
    [formValues, updateFieldValue],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestionIndex((p) =>
          p < languageSuggestions.length - 1 ? p + 1 : p,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestionIndex((p) => (p > 0 ? p - 1 : -1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const toAdd =
          selectedSuggestionIndex >= 0
            ? languageSuggestions[selectedSuggestionIndex]
            : language.trim();
        const current = formValues["language"] || [];
        if (toAdd && !current.includes(toAdd)) {
          updateFieldValue("language", [...current, toAdd]);
        }
        setLanguage("");
        setShowLanguageSuggestions(false);
        setSelectedSuggestionIndex(-1);
      } else if (e.key === "Escape") {
        setShowLanguageSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    },
    [
      language,
      languageSuggestions,
      selectedSuggestionIndex,
      formValues,
      updateFieldValue,
    ],
  );

  const languageSuggestionMouseDown = useRef(false);
  const locationSuggestionMouseDown = useRef(false);

  const handleInputBlur = useCallback(() => {
    if (languageSuggestionMouseDown.current) return;
    setTimeout(() => {
      setShowLanguageSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }, 200);
  }, []);

  const handleInputFocus = useCallback(() => {
    if (language.trim() && languageSuggestions.length > 0)
      setShowLanguageSuggestions(true);
  }, [language, languageSuggestions]);

  // ── Experience handlers ──
  const handleExperienceChange = useCallback(
    (e) => {
      setIsExperience(e.target.checked);
      if (e.target.checked) {
        updateFieldValue("experience", "");
        setExperienceDisplay("");
      }
    },
    [updateFieldValue],
  );

  const handleExperienceInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (value === "") {
        setExperienceDisplay("");
        updateFieldValue("experience", "");
        return;
      }
      const cleanValue = value.replace(/,/g, "");
      if (/^\d*\.?\d*$/.test(cleanValue)) {
        setExperienceDisplay(value);
        updateFieldValue("experience", cleanValue);
      }
    },
    [updateFieldValue],
  );

  // ── CTC handlers ──
  const handleCurrentCTCCurrencyChange = useCallback(
    (e) => {
      const curr = formValues["currentCTC"] || {
        amount: "",
        currencyCode: "INR",
      };
      updateFieldValue("currentCTC", { ...curr, currencyCode: e.target.value });
      const expCurr = formValues["expectedCTC"] || {
        amount: "",
        currencyCode: "INR",
      };
      updateFieldValue("expectedCTC", {
        ...expCurr,
        currencyCode: e.target.value,
      });
    },
    [formValues, updateFieldValue],
  );

  const handleExpectedCTCCurrencyChange = useCallback(
    (e) => {
      const curr = formValues["expectedCTC"] || {
        amount: "",
        currencyCode: "INR",
      };
      updateFieldValue("expectedCTC", {
        ...curr,
        currencyCode: e.target.value,
      });
    },
    [formValues, updateFieldValue],
  );

  const handleCurrentCTCChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (/^\d*$/.test(value.replace(/,/g, ""))) {
        const num = getNumericValue(value);
        setCurrentCTCDisplay(formatNumberWithCommas(num));
        const curr = formValues["currentCTC"] || {
          amount: "",
          currencyCode: "INR",
        };
        updateFieldValue("currentCTC", { ...curr, amount: num });
      }
    },
    [formValues, updateFieldValue],
  );

  const handleExpectedCTCChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (/^\d*$/.test(value.replace(/,/g, ""))) {
        const num = getNumericValue(value);
        setExpectedCTCDisplay(formatNumberWithCommas(num));
        const curr = formValues["expectedCTC"] || {
          amount: "",
          currencyCode: "INR",
        };
        updateFieldValue("expectedCTC", { ...curr, amount: num });
      }
    },
    [formValues, updateFieldValue],
  );

  // ── Location handlers ──
  const fetchLocationSuggestions = useCallback(
    async (query) => {
      if (!query.trim()) {
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
        return;
      }
      setIsLoadingLocations(true);
      try {
        const response = await apiClient.get("/locations/search", {
          params: { q: query, limit: 10, company_id: companyId },
        });
        const suggestions = response?.data?.results || [];
        setLocationSuggestions(suggestions);
        setShowLocationSuggestions(suggestions.length > 0);
      } catch (error) {
        console.error("Error fetching location suggestions:", error);
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
      } finally {
        setIsLoadingLocations(false);
      }
    },
    [companyId],
  );

  useEffect(() => {
    fetchLocationSuggestions(location);
  }, [location, fetchLocationSuggestions]);

  const handleLocationSuggestionClick = useCallback(
    (suggestion) => {
      const current = formValues["preferred_locations"] || [];
      if (!current.includes(suggestion)) {
        updateFieldValue("preferred_locations", [...current, suggestion]);
      }
      setLocation("");
      setShowLocationSuggestions(false);
      setSelectedLocationSuggestionIndex(-1);
    },
    [formValues, updateFieldValue],
  );

  const handleLocationKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedLocationSuggestionIndex((p) =>
          p < locationSuggestions.length - 1 ? p + 1 : p,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedLocationSuggestionIndex((p) => (p > 0 ? p - 1 : -1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const toAdd =
          selectedLocationSuggestionIndex >= 0
            ? locationSuggestions[selectedLocationSuggestionIndex]
            : location.trim();
        const current = formValues["preferred_locations"] || [];
        if (toAdd && !current.includes(toAdd)) {
          updateFieldValue("preferred_locations", [...current, toAdd]);
        }
        setLocation("");
        setShowLocationSuggestions(false);
        setSelectedLocationSuggestionIndex(-1);
      } else if (e.key === "Escape") {
        setShowLocationSuggestions(false);
        setSelectedLocationSuggestionIndex(-1);
      }
    },
    [
      location,
      locationSuggestions,
      selectedLocationSuggestionIndex,
      formValues,
      updateFieldValue,
    ],
  );

  const handleRemoveLocation = useCallback(
    (loc) => {
      updateFieldValue(
        "preferred_locations",
        (formValues["preferred_locations"] || []).filter((l) => l !== loc),
      );
    },
    [formValues, updateFieldValue],
  );

  const handleLocationPreferenceChange = useCallback(
    (e) => {
      setIsLocationPreference(e.target.checked);
      setLocation("");
      updateFieldValue("preferred_locations", []);
    },
    [updateFieldValue],
  );

  // ── WFH handlers ──
  const handleWfhCheckboxChange = useCallback(
    (e) => {
      if (e.target.checked) {
        setPendingWfh(true);
        setShowConfirmationModal(true);
      } else {
        setIsWFH(false);
        updateFieldValue("isWFH", false);
      }
    },
    [updateFieldValue],
  );

  // ── Resume handlers ──
  const handleResumeUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowed.includes(file.type)) {
      setResumeError("Only PDF or Word documents are accepted.");
      setResumeFile(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setResumeError("File size must be under 5 MB.");
      setResumeFile(null);
      return;
    }
    setResumeError("");
    setResumeFile(file);
  }, []);

  const handleRemoveResume = useCallback(() => {
    setResumeFile(null);
    setResumeError("");
  }, []);

  // ── DnD sensors ──
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: () => {} }),
  );

  const handleDragStart = useCallback(
    (event) => setActiveId(event.active.id),
    [],
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        const locs = formValues["preferred_locations"] || [];
        const oldIndex = locs.indexOf(active.id);
        const newIndex = locs.indexOf(over.id);
        updateFieldValue(
          "preferred_locations",
          arrayMove(locs, oldIndex, newIndex),
        );
      }
      setActiveId(null);
    },
    [formValues, updateFieldValue],
  );

  // ── Form submit ──
  const handleSubmit = useCallback(async () => {
    if (isSubmitting || showLoader) return;

    if (!resumeFile) {
      showSnackbar("Please upload your resume before submitting.", "error");
      return;
    }

    // Validate dynamic fields
    const newFieldErrors = {};
    sortedFields.forEach((field) => {
      // Skip preferred_locations if no-preference is checked
      if (field.id === "preferred_locations" && isLocationPreference) return;
      // Skip experience if zero-experience is checked
      if (field.id === "experience" && isExperience) return;
      const error = validateDynamicField(field, formValues[field.id]);
      if (error) newFieldErrors[field.id] = error;
    });

    if (!consentGiven) {
      newFieldErrors._consent =
        "By submitting this form, you must consent to storing your personal details.";
    }

    setFieldErrors(newFieldErrors);

    if (Object.keys(newFieldErrors).length > 0) {
      const firstErrorId = Object.keys(newFieldErrors)[0];
      const el = document.getElementById(`field-${firstErrorId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      const msgs = Object.values(newFieldErrors).filter(Boolean);
      showSnackbar(msgs, "error");
      return;
    }

    setIsSubmitting(true);
    setShowLoader(true);

    try {
      // ── Build dynamic answers array ─────────────
      const answers = sortedFields
        .filter((field) => field.numericId != null) // skip fields without a server id
        .map((field) => {
          const raw = formValues[field.id];

          // Serialise value to what the API expects in input.answer
          let answer;
          switch (field.type) {
            case "number":
              answer = parseInt(raw, 10) || 0;
              break;
            case "textarea":
            case "phone":
            case "listOfValuesText":
            case "multipleOptionsSingleSelection":
              answer = raw ?? "";
              break;
            case "textOnlyMultipleEntries":
            case "multiChipInput":
            case "multipleOptionsMultipleSelections":
              answer = Array.isArray(raw) ? raw : [];
              break;
            case "listOfValuesNumbers":
              answer = parseInt(raw, 10) || 0;
              break;
            case "countryList":
            case "usStateList":
            case "indianStateList":
            case "canadianStateList":
            case "australianStateList":
              answer = raw || "";
              break;
            case "sortableChipInput":
              answer = Array.isArray(raw) ? raw : [];
              break;
            case "singleDate":
            case "date":
              if (field.id === "dob" && raw) {
                const [y, m, d] = raw.split("-");
                answer = `${d}-${m}-${y}`; // "dd-mm-yyyy"
              } else {
                answer = raw || null; // "YYYY-MM-DD" string
              }
              break;
            case "multipleDates":
              answer = Array.isArray(raw) ? raw : [];
              break;
            case "dateRange":
              answer = {
                start_date: raw?.from || null,
                end_date: raw?.to || null,
              };
              break;
            case "currency":
              answer = {
                currency: raw?.currencyCode || "INR",
                value: parseInt(raw?.amount, 10) || 0,
              };
              break;
            case "checkbox":
              answer = Boolean(raw);
              break;
            default:
              answer = raw ?? "";
          }

          return {
            question_id: field.numericId,
            default_id: field.default_id, // mirrors question_id per API contract
            input: { answer },
          };
        });

      const response = await apiService.extractPublicFormData({
        jobDescriptionId: urlParams.jobDescriptionId,
        email: emailValue,
        templateId: normalizedFields[0]?.template_id || 1,
        answers,
        resume: resumeFile,
      });

      setTimeout(() => {
        setIsSubmitting(false);
        setShowLoader(false);
        navigate("/welcome-response", {
          state: {
            jobDescriptionId: urlParams.jobDescriptionId,
            lastDate: response?.last_date_used || null,
            candidateName: response?.candidate_name || null,
            emailSentTo: response?.email_sent_to || null,
            interviewId: response?.interview_id || null,
            companyName: response?.company_name || null,
          },
        });
      }, 500);
    } catch (error) {
      showSnackbar(`Submission failed: ${error.message}`, "error");
      setIsSubmitting(false);
      setShowLoader(false);
    }
  }, [
    isSubmitting,
    showLoader,
    resumeFile,
    formik.values.phoneNumber,
    formValues,
    sortedFields,
    isExperience,
    isLocationPreference,
    isWFH,
    urlParams,
    emailValue,
    navigate,
    consentGiven,
    showSnackbar,
  ]);

  // ── Page helpers ──
  const handleOpenPrivacy = useCallback(
    () => window.open("/privacy-policy.html", "_blank"),
    [],
  );
  const handleOpenTerms = useCallback(
    () => window.open("/terms-of-use.html", "_blank"),
    [],
  );
  const addChipValue = (fieldId) => {
    const inputValue = inputValues[fieldId]?.trim();
    if (!inputValue) return;
    if (!formValues[fieldId].includes(inputValue)) {
      updateFieldValue(fieldId, [...formValues[fieldId], inputValue]);
    }
    setInputValues((prev) => ({ ...prev, [fieldId]: "" }));
  };

  const removeChipValue = (fieldId, value) => {
    updateFieldValue(
      fieldId,
      formValues[fieldId].filter((v) => v !== value),
    );
  };
  const bothVerified = otpStep === "verified" && emailStep === "emailVerified";
  useEffect(() => {
    if (normalizedFields.length === 0) {
      const fields = loadNormalizedFieldsFromSession();
      if (fields.length > 0) setNormalizedFields(fields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bothVerified]);
  // ─── Dynamic field renderer (mirrors Welcome.js)  ───────────────────────────
  const renderField = (field) => {
    const value = formValues[field.id];

    // ── List-backed dropdowns (countries, states…) ──
    if (LIST_TYPES.includes(field.type)) {
      const options = FIELD_OPTIONS[field.type] || [];
      return (
        <select
          value={value}
          onChange={(e) => updateFieldValue(field.id, e.target.value)}
          className="border p-2 rounded w-full max-w-[450px]"
        >
          <option value="">Select</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }

    switch (field.type) {
      // ── Text ──
      case "text":
      case "textOnly":
      case "textIncludingNumbers":
        return (
          <input
            type="text"
            placeholder={field.placeholder || ""}
            value={value}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] focus:outline-none text-allfonts placeholder-gray-500 px-2 py-[3.5px] w-full max-w-[450px] placeholder:text-[#CCCCCC]"
            style={{ borderWidth: "0.25px" }}
          />
        );
        return (
          <input
            type="text"
            placeholder={field.placeholder || ""}
            value={value}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="border p-2 rounded w-full max-w-[450px] placeholder:text-[#CCCCCC]"
          />
        );

      // ── Number ──
      case "number":
        return (
          <div className="flex items-center gap-2 ">
            <input
              type="text"
              placeholder={field.placeholder || ""}
              value={field.id === "experience" ? experienceDisplay : value}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^\d*\.?\d*$/.test(v.replace(/,/g, ""))) {
                  if (field.id === "experience") setExperienceDisplay(v);
                  updateFieldValue(field.id, v.replace(/,/g, ""));
                }
              }}
              className="bg-[#F9FAFB] border border-[#666666] focus:outline-none text-allfonts px-2 py-[3.5px] placeholder:text-[#CCCCCC] width-450 "
              style={{ borderRadius: "4px", borderWidth: "0.25px" }}
            />
            {field.suffix && (
              <span className="text-allfonts text-fontColor whitespace-nowrap">
                {field.suffix}
              </span>
            )}
          </div>
        );

      // ── List of Values Numbers (multi-select, same UI as multipleOptionsMultipleSelections) ──
      case "listOfValuesNumbers":
        {
          const selected = Array.isArray(value) ? value : [];
          const searchKey = `__lvn_search_${field.id}`;
          const isOpenKey = `__lvn_open_${field.id}`;
          const searchTerm = (inputValues[searchKey] || "").toLowerCase();
          const isOpen = !!inputValues[isOpenKey];
          const allOptions = field.options || [];
          const filtered = searchTerm
            ? allOptions.filter((o) =>
                String(o).toLowerCase().includes(searchTerm),
              )
            : allOptions;

          const toggleOption = (option) => {
            const next = selected.includes(option)
              ? selected.filter((o) => o !== option)
              : [...selected, option];
            updateFieldValue(field.id, next);
          };

          const removeChip = (option) => {
            updateFieldValue(
              field.id,
              selected.filter((o) => o !== option),
            );
          };

          return (
            <div className="flex flex-col gap-2 w-full max-w-[450px]">
              {/* ── Trigger / search input ── */}
              <div className="relative">
                <div
                  className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] px-2 py-[3.5px] flex items-center gap-2 cursor-text w-full max-w-[450px]"
                  style={{ borderWidth: "0.25px", minHeight: "32px" }}
                  onClick={() =>
                    setInputValues((prev) => ({
                      ...prev,
                      [isOpenKey]: !prev[isOpenKey],
                      [searchKey]: "",
                    }))
                  }
                >
                  <input
                    type="text"
                    placeholder={
                      selected.length === 0
                        ? "Search and select…"
                        : `${selected.length} selected — search to add more`
                    }
                    value={inputValues[searchKey] || ""}
                    onChange={(e) => {
                      e.stopPropagation();
                      setInputValues((prev) => ({
                        ...prev,
                        [searchKey]: e.target.value,
                        [isOpenKey]: true,
                      }));
                    }}
                    onFocus={() =>
                      setInputValues((prev) => ({ ...prev, [isOpenKey]: true }))
                    }
                    onBlur={() =>
                      setTimeout(
                        () =>
                          setInputValues((prev) => ({
                            ...prev,
                            [isOpenKey]: false,
                            [searchKey]: "",
                          })),
                        200,
                      )
                    }
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 bg-transparent focus:outline-none text-allfonts placeholder:text-[#CCCCCC] min-w-0"
                    autoComplete="off"
                  />
                  {/* chevron */}
                  <svg
                    className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>

                {/* ── Dropdown list ── */}
                {isOpen && (
                  <div
                    className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-52 overflow-y-auto mt-0.5"
                    style={{ borderWidth: "0.25px" }}
                  >
                    {filtered.length === 0 ? (
                      <div className="px-3 py-2 text-allfonts text-gray-400 italic">
                        No options found
                      </div>
                    ) : (
                      filtered.map((option) => {
                        const isChecked = selected.includes(option);
                        return (
                          <div
                            key={option}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              toggleOption(option);
                            }}
                            className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-allfonts hover:bg-gray-50 ${
                              isChecked ? "bg-[#f0f3ff]" : ""
                            }`}
                          >
                            {/* checkbox indicator */}
                            <div
                              className={`w-4 h-4 shrink-0 rounded-sm border flex items-center justify-center ${
                                isChecked
                                  ? "bg-[#002147] border-[#002147]"
                                  : "border-gray-400 bg-white"
                              }`}
                            >
                              {isChecked && (
                                <svg
                                  className="w-3 h-3 text-white"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                >
                                  <path
                                    d="M2 6l3 3 5-5"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </div>
                            <span
                              className={
                                isChecked
                                  ? "font-medium text-[#002147]"
                                  : "text-fontColor"
                              }
                            >
                              {option}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* ── Selected chips box ── */}
              {selected.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {selected.map((item) => (
                    <div
                      key={item}
                      className="bg-gray-100 border border-[#CCCCCC] px-3 py-1 rounded-full flex items-center gap-2 text-allfonts"
                      style={{ borderWidth: "0.25px" }}
                    >
                      <span>{item}</span>
                      <button
                        type="button"
                        onClick={() => removeChip(item)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }
        return (
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder={field.placeholder || ""}
              value={value}
              onChange={(e) => updateFieldValue(field.id, e.target.value)}
              className="border p-2 rounded w-[200px] placeholder:text-[#CCCCCC]"
            />
            {field.suffix && (
              <span className="text-gray-500 text-sm">{field.suffix}</span>
            )}
          </div>
        );

      // ── Phone ──
      case "phone":
        return (
          <PhoneInput
            country={"in"}
            value={value}
            onChange={(val) => updateFieldValue(field.id, val)}
            inputStyle={{ width: "100%", maxWidth: "450px" }}
          />
        );

      // ── Single Date ──
      case "date":
      case "singleDate": {
        const today = new Date();
        const todayStr = today.toISOString().split("T")[0];

        // Detect whether min/max are "today", a YYYY-MM-DD string, or a numeric age offset
        const isDateLikeSingle = (v) =>
          v === "today" ||
          (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v));

        let minDate = null;
        let maxDate = null;

        if (isDateLikeSingle(field.min) || isDateLikeSingle(field.max)) {
          // Direct date bounds (e.g. "When are you planning to leave")
          minDate =
            field.min === "today"
              ? todayStr
              : field.min != null
                ? field.min
                : null;
          maxDate =
            field.max === "today"
              ? todayStr
              : field.max != null
                ? field.max
                : null;
        } else {
          // Numeric age offsets → invert for HTML date input
          // field.min = min age (e.g. 18) → HTML max (can't pick too recent)
          // field.max = max age (e.g. 90) → HTML min (can't pick too far back)
          maxDate =
            field.min != null
              ? new Date(
                  today.getFullYear() - field.min,
                  today.getMonth(),
                  today.getDate(),
                )
                  .toISOString()
                  .split("T")[0]
              : null;
          minDate =
            field.max != null
              ? new Date(
                  today.getFullYear() - field.max,
                  today.getMonth(),
                  today.getDate(),
                )
                  .toISOString()
                  .split("T")[0]
              : null;
        }

        return (
          <CustomDatePicker
            name={field.id}
            value={value}
            min={minDate}
            max={maxDate}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
          />
        );
      }

      // ── Multiple Dates ──
      case "multipleDates": {
        const dates = Array.isArray(value) ? value : [];

        return (
          <div>
            <CustomDatePicker
              name={field.id}
              value=""
              min={field.min}
              max={field.max}
              onChange={(e) => {
                const picked = e.target.value;

                if (picked && !dates.includes(picked)) {
                  updateFieldValue(field.id, [...dates, picked]);
                }
              }}
            />

            <div className="flex flex-wrap gap-2 mt-3">
              {dates.map((d) => (
                <div
                  key={d}
                  className="bg-gray-100 border border-[#CCCCCC] px-3 py-1 rounded-full flex items-center gap-2 text-allfonts"
                  style={{ borderWidth: "0.25px" }}
                >
                  <span>{d}</span>

                  <button
                    type="button"
                    onClick={() =>
                      updateFieldValue(
                        field.id,
                        dates.filter((x) => x !== d),
                      )
                    }
                    className="text-gray-400 hover:text-red-500"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      }

      // ── Date Range ──
      case "dateRange": {
        const range =
          value && typeof value === "object" ? value : { from: "", to: "" };

        return (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex flex-col gap-1 w-full sm:w-auto">
              <label className="text-allfonts-small text-gray-500">From</label>

              <CustomDatePicker
                name={`${field.id}_from`}
                value={range.from}
                min={field.min}
                max={field.max}
                onChange={(e) =>
                  updateFieldValue(field.id, {
                    ...range,
                    from: e.target.value,
                  })
                }
              />
            </div>

            <span className="hidden sm:inline mt-5 text-gray-400">→</span>

            <div className="flex flex-col gap-1 w-full sm:w-auto">
              <label className="text-allfonts-small text-gray-500">To</label>

              <CustomDatePicker
                name={`${field.id}_to`}
                value={range.to}
                min={range.from || field.min}
                max={field.max}
                onChange={(e) =>
                  updateFieldValue(field.id, {
                    ...range,
                    to: e.target.value,
                  })
                }
              />
            </div>
          </div>
        );
      }

      // ── Dropdown (admin-defined options) ──
      case "dropdown":
      case "listOfValuesText":
      case "countryList":
      case "indianStateList":
      case "usStateList":
      case "canadianStateList":
      case "australianStateList":
        {
          const options = LIST_TYPES.includes(field.type)
            ? FIELD_OPTIONS[field.type] || []
            : field.options || [];
          // For large lists (countries / states) render a searchable select
          const isLargeList = LIST_TYPES.includes(field.type);
          if (isLargeList) {
            // Each large-list field gets its own local search term via inputValues
            const searchTerm = (
              inputValues[`__search_${field.id}`] || ""
            ).toLowerCase();
            const filtered = searchTerm
              ? options.filter((o) => o.toLowerCase().includes(searchTerm))
              : options;
            return (
              <div
                className="flex flex-col gap-1"
                style={{ maxWidth: "450px" }}
              >
                <input
                  type="text"
                  placeholder={`Search ${field.placeholder || "…"}`}
                  value={inputValues[`__search_${field.id}`] || ""}
                  onChange={(e) =>
                    setInputValues((prev) => ({
                      ...prev,
                      [`__search_${field.id}`]: e.target.value,
                    }))
                  }
                  className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] px-2 py-[3.5px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] w-full"
                  style={{ borderWidth: "0.25px" }}
                />
                <div className="relative">
                  <select
                    value={value}
                    onChange={(e) => updateFieldValue(field.id, e.target.value)}
                    size={6}
                    className={`bg-[#F9FAFB] w-full border border-[#666666] rounded-[4px] px-2 py-[3.5px] focus:outline-none text-allfonts ${
                      value ? "text-fontColor" : "text-[#CCCCCC]"
                    }`}
                    style={{ borderWidth: "0.25px" }}
                  >
                    <option value="" disabled>
                      {field.placeholder || "Select…"}
                    </option>
                    {filtered.map((opt) => (
                      <option key={opt} value={opt} className="text-black py-1">
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                {value && (
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-allfonts-small text-fontColor">
                      Selected:
                    </span>
                    <span className="bg-[#002147] text-white text-allfonts-small px-2 py-0.5 rounded-full">
                      {value}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateFieldValue(field.id, "")}
                      className="text-allfonts-small text-gray-400 hover:text-red-500 underline"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
            );
          }
          return (
            <div className="relative mt-2" style={{ maxWidth: "320px" }}>
              <select
                value={value}
                onChange={(e) => updateFieldValue(field.id, e.target.value)}
                className={`bg-[#F9FAFB] w-full max-w-[450px] border border-[#666666] rounded-[4px] px-2 py-[3.5px] focus:outline-none text-allfonts appearance-none pr-10 ${
                  value ? "text-fontColor" : "text-[#CCCCCC]"
                }`}
                style={{ borderWidth: "0.25px" }}
              >
                <option value="" disabled>
                  {field.placeholder || "Select an option"}
                </option>
                {options.map((opt) => (
                  <option key={opt} value={opt} className="text-black">
                    {opt}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                <svg
                  className="w-4 h-4 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          );
        }
        return (
          <select
            value={value}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="border p-2 rounded w-full max-w-[450px]"
          >
            <option value="">Select</option>
            {(field.options || []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "radio":
      case "multipleOptionsSingleSelection":
        return (
          <div className="flex flex-wrap gap-6 mt-2">
            {(field.options || []).map((option) => (
              <label
                key={option}
                className="flex items-center space-x-2 text-allfonts cursor-pointer text-fontColor"
              >
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => updateFieldValue(field.id, e.target.value)}
                  className="w-5 h-5 "
                  style={{ accentColor: "#002147" }}
                />
                <span className="capitalize">{option}</span>
              </label>
            ))}
          </div>
        );
        return (
          <div className="flex gap-4 flex-wrap">
            {(field.options || []).map((option) => (
              <label
                key={option}
                className="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => updateFieldValue(field.id, e.target.value)}
                  style={{ accentColor: "#002147" }}
                />
                {option}
              </label>
            ))}
          </div>
        );

      // ── Checkbox ──
      case "checkbox":
        return (
          <label className="flex items-center space-x-2 text-allfonts cursor-pointer text-fontColor">
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => updateFieldValue(field.id, e.target.checked)}
              className="transform scale-125"
              style={{ accentColor: "#002147" }}
            />
            <span>{field.label}</span>
          </label>
        );
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => updateFieldValue(field.id, e.target.checked)}
              style={{ accentColor: "#002147" }}
            />
            <span>{field.label}</span>
          </label>
        );

      // ── Currency ──
      case "currency": {
        const currencyFields = sortedFields.filter((f) => f.type === "currency");
        const hasMultipleCurrencyFields = currencyFields.length > 1;

        // Determine master field: explicitly set, or the first currency field that has a currency selected
        const anyCurrencySelected = currencyFields.some(
          (f) => formValues[f.id]?.currencyCode,
        );
        const effectiveMasterId =
          currencyMasterFieldId ||
          (anyCurrencySelected ? currencyFields[0]?.id : null);

        const isLocked =
          hasMultipleCurrencyFields &&
          effectiveMasterId !== null &&
          field.id !== effectiveMasterId;

        const curr =
          value && typeof value === "object"
            ? value
            : { amount: "", currencyCode: "INR" };

        // If locked, ensure currencyCode matches the master field's currency
        const activeCurrencyCode =
          isLocked && effectiveMasterId
            ? formValues[effectiveMasterId]?.currencyCode || curr.currencyCode || ""
            : curr.currencyCode || "";

        // Format the stored raw number for display
        const displayAmount = curr.amount
          ? curr.amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          : "";

        return (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2">
            <div className="relative w-full max-w-[260px] md:max-w-[180px]">
              <select
                style={{ borderWidth: "0.25px", borderRadius: "4px" }}
                disabled={isLocked}
                title={
                  isLocked
                    ? "Locked to selected currency across all fields"
                    : undefined
                }
                className={`w-full appearance-none px-2 pr-8 py-[3.5px] focus:outline-none text-allfonts ${
                  isLocked
                    ? "bg-[#E5E7EB] text-gray-500 cursor-not-allowed border-[#CCCCCC]"
                    : `bg-[#F9FAFB] border border-[#666666] ${
                        activeCurrencyCode ? "text-fontColor" : "text-[#CCCCCC]"
                      }`
                }`}
                value={activeCurrencyCode}
                onChange={(e) => handleCurrencyChange(field.id, e.target.value)}
              >
                <option value="" disabled>
                  Currency
                </option>

                {currenciesOption.map((c) => (
                  <option key={c.code} value={c.code} className="text-black">
                    {c.name} {c.symbol}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                {isLocked ? (
                  <svg
                    className="w-3.5 h-3.5 text-gray-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-4 h-4 text-gray-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
            </div>
            <input
              type="text"
              placeholder="CTC"
              value={displayAmount}
              onChange={(e) => {
                const raw = e.target.value.replace(/,/g, "");
                if (/^\d*$/.test(raw)) {
                  updateFieldValue(field.id, {
                    ...curr,
                    currencyCode: activeCurrencyCode,
                    amount: raw,
                  });
                }
              }}
              style={{
                borderWidth: "0.25px",
                borderRadius: "4px",
              }}
              className="bg-[#F9FAFB] border border-[#666666] px-2 py-[3.5px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] w-full max-w-[260px]"
            />
            <p className="text-allfonts text-fontColor whitespace-nowrap shrink-0">
             {field.suffix || "Per Annum"}
            </p>
          </div>
        );
      }
      case "ethnicityDropdown":
        return (
          <div className="relative mt-2" style={{ maxWidth: "320px" }}>
            <select
              value={value}
              onChange={(e) => updateFieldValue(field.id, e.target.value)}
              className={`bg-[#F9FAFB] w-full max-w-[450px] border border-[#666666] rounded-[4px] px-2 py-[3.5px] focus:outline-none text-allfonts appearance-none pr-10 ${
                value ? "text-fontColor" : "text-[#CCCCCC]"
              }`}
              style={{ borderWidth: "0.25px" }}
            >
              <option value="" disabled>
                {field.placeholder || "Select ethnicity"}
              </option>

              {(field.options?.length
                ? field.options
                : ethnicityOptions.map((o) => o.name)
              ).map((opt) => (
                <option key={opt} value={opt} className="text-black">
                  {opt}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              <svg
                className="w-4 h-4 text-gray-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </div>
        );
      // ── Multi Chip Input (unordered tags) ──
      // If this is the language field (semantic id = "language"), use the
      // language typeahead + API suggestions. For any other text_multiple_entries
      // field, fall through to the generic chip input below.
      case "multiChipInput":
      case "textOnlyMultipleEntries":
        if (field.id === "language") {
          return (
            <div className="w-full max-w-[450px]">
              <div className="relative">
                <input
                  type="text"
                  placeholder={field.placeholder || "Start typing…"}
                  value={language}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleInputBlur}
                  onFocus={handleInputFocus}
                  className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] px-2 py-[3.5px] w-full"
                  autoComplete="off"
                  style={{ borderWidth: "0.25px" }}
                />
                {isLoadingLanguages && (
                  <div className="absolute right-3 top-1 text-gray-400 text-allfonts">
                    Loading...
                  </div>
                )}
                {showLanguageSuggestions && languageSuggestions.length > 0 && (
                  <div
                    className="absolute top-full left-0 w-full bg-white border text-allfonts border-gray-300 rounded-md shadow-lg z-50 max-h-48 overflow-y-auto mt-0.5"
                    onMouseDown={() => {
                      languageSuggestionMouseDown.current = true;
                    }}
                    onMouseUp={() => {
                      languageSuggestionMouseDown.current = false;
                    }}
                    onTouchStart={() => {
                      languageSuggestionMouseDown.current = true;
                    }}
                    onTouchEnd={() => {
                      languageSuggestionMouseDown.current = false;
                    }}
                  >
                    {languageSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className={`px-3 py-2 cursor-pointer text-allfonts hover:bg-gray-100 ${index === selectedSuggestionIndex ? "bg-blue-100" : ""}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onPointerDown={(e) => e.preventDefault()}
                        onClick={() => handleSuggestionClick(suggestion)}
                        onMouseEnter={() => setSelectedSuggestionIndex(index)}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div
                className={`flex flex-wrap gap-2 text-allfonts ${Array.isArray(value) && value.length > 0 ? "mt-2" : ""}`}
              >
                {(Array.isArray(value) ? value : []).map((item) => (
                  <CommonChip
                    key={item}
                    unitName={item}
                    onRemove={handleRemoveLanguage}
                    color={false}
                  />
                ))}
              </div>
            </div>
          );
        }
        // Generic text_multiple_entries — plain chip input with Enter-to-add
        return (
          <div className="w-full max-w-[450px]">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={field.placeholder || "Type and press Enter"}
                value={inputValues[field.id] || ""}
                onChange={(e) =>
                  setInputValues((prev) => ({
                    ...prev,
                    [field.id]: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addChipValue(field.id);
                  }
                }}
                className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] px-2 py-[3.5px] w-full"
                style={{ borderWidth: "0.25px" }}
              />
            </div>
            <div
              className={`flex flex-wrap gap-2 text-allfonts ${Array.isArray(value) && value.length > 0 ? "mt-2" : ""}`}
            >
              {(Array.isArray(value) ? value : []).map((item) => (
                <CommonChip
                  key={item}
                  unitName={item}
                  onRemove={(v) => removeChipValue(field.id, v)}
                  color={false}
                />
              ))}
            </div>
          </div>
        );
      // ── Sortable Chip Input (ordered, draggable tags) ──
      case "sortableChipInput": {
        const locs = formValues[field.id] || [];
        return (
          <div className="flex flex-col w-full max-w-[450px]">
            <div className="relative">
              <input
                type="text"
                placeholder={field.placeholder || "Location"}
                value={location}
                onChange={(e) => setLocation(formatText(e.target.value))}
                onKeyDown={handleLocationKeyDown}
                onBlur={() => {
                  if (locationSuggestionMouseDown.current) return;
                  setTimeout(() => {
                    setShowLocationSuggestions(false);
                    setSelectedLocationSuggestionIndex(-1);
                  }, 200);
                }}
                onFocus={() => {
                  if (location.trim() && locationSuggestions.length > 0)
                    setShowLocationSuggestions(true);
                }}
                className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] px-2 py-[3.5px] w-full"
                autoComplete="off"
                style={{ borderWidth: "0.25px" }}
              />
              {isLoadingLocations && (
                <div className="absolute right-3 top-1 text-gray-400 text-allfonts">
                  Loading...
                </div>
              )}
              {showLocationSuggestions && locationSuggestions.length > 0 && (
                <div
                  className="absolute top-full left-0 w-full bg-white border text-allfonts border-gray-300 rounded-md shadow-lg z-50 max-h-48 overflow-y-auto mt-0.5"
                  onMouseDown={() => {
                    locationSuggestionMouseDown.current = true;
                  }}
                  onMouseUp={() => {
                    locationSuggestionMouseDown.current = false;
                  }}
                  onTouchStart={() => {
                    locationSuggestionMouseDown.current = true;
                  }}
                  onTouchEnd={() => {
                    locationSuggestionMouseDown.current = false;
                  }}
                >
                  {locationSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className={`px-3 py-2 cursor-pointer text-allfonts hover:bg-gray-100 ${index === selectedLocationSuggestionIndex ? "bg-blue-100" : ""}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onPointerDown={(e) => e.preventDefault()}
                      onClick={() => handleLocationSuggestionClick(suggestion)}
                      onMouseEnter={() =>
                        setSelectedLocationSuggestionIndex(index)
                      }
                    >
                      {formatText(suggestion)}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="w-full">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={locs}
                  strategy={verticalListSortingStrategy}
                >
                  <div
                    className={`flex flex-wrap gap-2 text-allfonts max-h-32 overflow-y-auto custom-scrollbar ${locs.length > 0 ? "mt-2" : ""}`}
                    style={{ backgroundColor: "#fff" }}
                  >
                    {locs.map((loc, index) => (
                      <DraggableChip
                        key={loc}
                        id={loc}
                        index={index}
                        number={index + 1}
                        label={loc}
                        onRemove={handleRemoveLocation}
                      />
                    ))}
                  </div>
                </SortableContext>
                <DragOverlay>
                  {activeId ? (
                    <div className="flex items-center bg-white border border-borderColor rounded-full px-1 py-[4px] shadow-lg opacity-90">
                      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center bg-btnBgColor rounded-full text-white text-tertiaryFontSize mr-2">
                        {locs.indexOf(activeId) + 1}
                      </div>
                      <div className="mr-2 select-none">{activeId}</div>
                      <div className="flex-shrink-0 p-1 rounded-full">
                        <img
                          src={removeImg}
                          alt="remove"
                          className="w-[10px] h-[10px]"
                        />
                      </div>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </div>
        );
      }

      // ── Multiple Options Multiple Selections (checkbox group) ──
      case "multipleOptionsMultipleSelections": {
        const selected = Array.isArray(value) ? value : [];
        const searchKey = `__mms_search_${field.id}`;
        const isOpenKey = `__mms_open_${field.id}`;
        const searchTerm = (inputValues[searchKey] || "").toLowerCase();
        const isOpen = !!inputValues[isOpenKey];
        const allOptions = field.options || [];
        const filtered = searchTerm
          ? allOptions.filter((o) => o.toLowerCase().includes(searchTerm))
          : allOptions;

        const toggleOption = (option) => {
          const next = selected.includes(option)
            ? selected.filter((o) => o !== option)
            : [...selected, option];
          updateFieldValue(field.id, next);
        };

        const removeChip = (option) => {
          updateFieldValue(
            field.id,
            selected.filter((o) => o !== option),
          );
        };

        return (
          <div className="flex flex-col gap-2 w-full max-w-[450px]">
            {/* ── Trigger / search input ── */}
            <div className="relative">
              <div
                className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] px-2 py-[3.5px] flex items-center gap-2 cursor-text w-full"
                style={{ borderWidth: "0.25px", minHeight: "32px" }}
                onClick={() =>
                  setInputValues((prev) => ({
                    ...prev,
                    [isOpenKey]: !prev[isOpenKey],
                    [searchKey]: "",
                  }))
                }
              >
                <input
                  type="text"
                  placeholder={
                    selected.length === 0
                      ? "Search and select…"
                      : `${selected.length} selected — search to add more`
                  }
                  value={inputValues[searchKey] || ""}
                  onChange={(e) => {
                    e.stopPropagation();
                    setInputValues((prev) => ({
                      ...prev,
                      [searchKey]: e.target.value,
                      [isOpenKey]: true,
                    }));
                  }}
                  onFocus={() =>
                    setInputValues((prev) => ({ ...prev, [isOpenKey]: true }))
                  }
                  onBlur={() =>
                    setTimeout(
                      () =>
                        setInputValues((prev) => ({
                          ...prev,
                          [isOpenKey]: false,
                          [searchKey]: "",
                        })),
                      200,
                    )
                  }
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 bg-transparent focus:outline-none text-allfonts placeholder:text-[#CCCCCC] min-w-0"
                  autoComplete="off"
                />
                {/* chevron */}
                <svg
                  className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              {/* ── Dropdown list ── */}
              {isOpen && (
                <div
                  className="absolute top-full left-0 w-full bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-52 overflow-y-auto mt-0.5"
                  style={{ borderWidth: "0.25px" }}
                >
                  {filtered.length === 0 ? (
                    <div className="px-3 py-2 text-allfonts text-gray-400 italic">
                      No options found
                    </div>
                  ) : (
                    filtered.map((option) => {
                      const isChecked = selected.includes(option);
                      return (
                        <div
                          key={option}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            toggleOption(option);
                          }}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-allfonts hover:bg-gray-50 ${
                            isChecked ? "bg-[#f0f3ff]" : ""
                          }`}
                        >
                          {/* checkbox indicator */}
                          <div
                            className={`w-4 h-4 shrink-0 rounded-sm border flex items-center justify-center ${
                              isChecked
                                ? "bg-[#002147] border-[#002147]"
                                : "border-gray-400 bg-white"
                            }`}
                          >
                            {isChecked && (
                              <svg
                                className="w-3 h-3 text-white"
                                viewBox="0 0 12 12"
                                fill="none"
                              >
                                <path
                                  d="M2 6l3 3 5-5"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                          <span
                            className={
                              isChecked
                                ? "font-medium text-[#002147]"
                                : "text-fontColor"
                            }
                          >
                            {option}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* ── Selected chips box ── */}
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-1">
                {selected.map((item) => (
                  <div
                    key={item}
                    className="bg-gray-100 border border-[#CCCCCC] px-3 py-1 rounded-full flex items-center gap-2 text-allfonts"
                    style={{ borderWidth: "0.25px" }}
                  >
                    <span>{item}</span>
                    <button
                      type="button"
                      onClick={() => removeChip(item)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      // ── Textarea (long text) ──
      case "textarea": {
        return (
          <textarea
            placeholder={field.placeholder || ""}
            value={value}
            rows={4}
            onChange={(e) => updateFieldValue(field.id, e.target.value)}
            className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] focus:outline-none text-allfonts placeholder:text-[#CCCCCC] px-2 py-[6px] w-full max-w-[450px] resize-y"
            style={{ borderWidth: "0.25px" }}
          />
        );
      }

      // ── Fallback ──
      default:
        return (
          <div className="text-sm text-red-400 italic">
            Unsupported field type: <code>{field.type}</code>. Add a case in{" "}
            <code>renderField()</code>.
          </div>
        );
    }
  };

  // ─── Inline modals ─────────────────────────────────────────────────────────
  const PhoneConfirmationModal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4">
      <div className="bg-white rounded-md shadow-xl w-[300px] md:w-[460px] max-w-full overflow-hidden">
        {/* Header bar — matches camera/mic/speaker confirmation modals */}
        <div className="flex items-center justify-between px-4 py-2 bg-[#E7E9EB] border-b border-[#CCCCCC]">
          <p className="font-medium text-sm text-[#111729]">Confirming...</p>
          <button
            onClick={() => setShowPhoneConfirmation(false)}
            className="text-gray-500"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-6 flex flex-col items-center text-[#111729]">
          <p className="text-sm mb-2 text-center">
            Are you sure you have entered the correct Phone Number?
          </p>
          <p className="font-semibold mb-6 text-center">
            +{formik.values.phoneNumber}
          </p>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowPhoneConfirmation(false)}
              className="px-6 sm:px-8 py-2 border rounded-md flex items-center space-x-1 text-sm"
              style={{ border: "1px solid #002147" }}
            >
              <img src={removeDarkImg} alt="No" className="w-3 h-3 mr-2" />
              <span>No</span>
            </button>
            <button
              onClick={handleSendOtp}
              className="px-6 sm:px-8 py-2 bg-[#111729] text-white rounded-md flex items-center space-x-1 text-sm"
            >
              <img src={rightImg} alt="Yes" className="w-4 h-4 mr-2" />
              <span>Yes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const WFHConfirmationModal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4">
      <div className="bg-white rounded-md shadow-xl w-[300px] md:w-[460px] max-w-full overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 bg-[#E7E9EB] border-b border-[#CCCCCC]">
          <p className="font-medium text-sm text-[#111729]">Confirmation</p>
          <button
            onClick={() => {
              setShowConfirmationModal(false);
              setPendingWfh(false);
            }}
            className="text-gray-500"
          >
            ✕
          </button>
        </div>

        <div className="p-6 flex flex-col items-center text-[#111729]">
          <p className="text-sm mb-6 text-center">
            Are you sure you want to opt for work from home as your primary preference?
          </p>
          <div className="flex space-x-3">
            <button
              className="px-6 sm:px-8 py-2 border rounded-md flex items-center space-x-1 text-sm"
              style={{ border: "1px solid #002147" }}
              onClick={() => {
                setShowConfirmationModal(false);
                setPendingWfh(false);
              }}
            >
              <img src={removeDarkImg} alt="no" className="w-3 h-3 mr-2" />
              <span>No</span>
            </button>
            <button
              className="px-6 sm:px-8 py-2 bg-[#111729] text-white rounded-md flex items-center space-x-1 text-sm"
              onClick={() => {
                setShowConfirmationModal(false);
                setIsWFH(true);
                updateFieldValue("isWFH", true);
              }}
            >
              <img src={rightImg} alt="yes" className="w-4 h-4 mr-2" />
              <span>Yes</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col bg-[#F9FAFB] overflow-hidden">
      {/* ── Phone dropdown: position:fixed so it escapes overflow-hidden parents ── */}
      <style>{`
        .react-tel-input .country-list {
          position: fixed !important;
          z-index: 9999 !important;
          max-height: 200px !important;
          overflow-y: auto !important;
          background-color: #ffffff !important;
          border: 1px solid #cccccc !important;
          border-radius: 6px !important;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15) !important;
          padding: 4px 0 !important;
        }
        .react-tel-input .flag-dropdown.open {
          z-index: 9999;
        }
        .react-tel-input .selected-flag {
          border: .25px solid black !important;
          border-right:none !important
        
        }
      `}</style>
      {isOtpSending && (
        <LoadingDialog
          title="Phone Verification"
          primaryMSG="Sending OTP to your"
          secondaryMSG={
            <span>
              Phone: <span>{maskPhoneNumber(formik.values.phoneNumber)}</span>
            </span>
          }
        />
      )}
      {isEmailOtpSending && (
        <LoadingDialog
          title="Sending OTP"
          primaryMSG="Sending OTP to your email..."
          bottomMsg="This will take just a bit."
        />
      )}
      {isOtpVerifying && (
        <LoadingDialog
          title="Verifying OTP"
          primaryMSG="Verifying OTP..."
          bottomMsg="This will take just a bit."
        />
      )}
      {isEmailOtpVerifying && (
        <LoadingDialog
          title="Verifying Email OTP"
          primaryMSG="Verifying email OTP..."
          bottomMsg="This will take just a bit."
        />
      )}
      {showLoader && (
        <LoadingDialog
          title="Submitting Information"
          primaryMSG="Processing your information..."
          bottomMsg="This will take a few seconds."
        />
      )}

      {/* ── Modals ── */}
      {showPhoneConfirmation && PhoneConfirmationModal}
      {showConfirmationModal && WFHConfirmationModal}

      {/* ── Header ── */}
      <div
        className="p-4 px-4 border-b border-[#CCCCCC] bg-[#ffffff]"
        style={{ borderBottomWidth: "0.25px" }}
      >
        <Header />
      </div>

      {/* ── Scrollable content ── */}
      <div
        className={`flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-4 bg-[#F9FAFB] top-1 max-h-[calc(100vh-110px)] mt-[16px] ${bothVerified ? "mb-[74px]" : "mb-[16px]"} w-full max-w-full`}
      >
        {/* Intro */}
        <div className="pb-6">
          <h1 className="app-title font-medium mb-4 mt-2 text-fontColor">
            Welcome{" "}
            {sessionStorage.getItem("candidateName") ||
              authService.getCandidateName() ||
              ""}
            !
          </h1>
          <p className="font-medium text-questionPrimaryFontSize text-fontColor">
            You are applying for -
          </p>
          <p className="font-medium text-[23px] text-fontColor">
            {data?.role
              ?.split(" ")
              .map(
                (word) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
              )
              .join(" ")}
          </p>
          <p className="font-medium text-[23px] mb-6 text-fontColor">
            {data?.company_name}
          </p>

          <p className="font-medium text-lessHeroFontSize mb-2 text-fontColor">
            Before we get started, we need a few basic information from you.
            Please provide the following details.
          </p>
          <p className="text-allfonts-small text-fontColor mb-2">
            Please note, your data is safe! None of your Personal details will
            be used for any other purposes other than this Assessment. All
            Information is encrypted and does not leave our systems, ever.
          </p>
          <p className="text-allfonts-small text-fontColor">
            To know more, Please read our{" "}
            <button
              onClick={handleOpenPrivacy}
              className="underline cursor-pointer text-[#3428C7]"
            >
              Data Privacy Policy
            </button>{" "}
            and{" "}
            <button
              onClick={handleOpenTerms}
              className="underline cursor-pointer text-[#3428C7]"
            >
              Terms of Use
            </button>{" "}
            documents.
          </p>
        </div>

        {/* ── Form fields ── */}
        <div className="flex flex-col gap-4 w-full">
          {/* ── 1. Email Address ── */}
          <div
            className="flex items-start gap-3 sm:gap-4 border border-[#CCCCCC] rounded-xl bg-white p-3 sm:p-4 md:p-6 w-full md:w-[75%]"
            style={{ borderWidth: "0.25px" }}
          >
            <CountBox count={1} />
            <div className="flex-1 min-w-0">
              <SectionLabel label="Email Address" required />
              <p className="text-allfonts-small italic mb-3 text-fontColor">
                * We will send a One-Time-PIN (OTP) to verify your Email.
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="w-full max-w-[450px]">
                    <input
                      type="email"
                      placeholder="Email Address"
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value)}
                      disabled={
                        emailStep === "emailOtp" ||
                        emailStep === "emailVerified"
                      }
                      className="bg-[#F9FAFB] border border-[#666666] rounded-[4px] focus:outline-none text-allfonts placeholder-gray-500 px-2 py-[3.5px] w-full placeholder:text-[#CCCCCC]"
                      style={{ borderWidth: "0.25px" }}
                    />
                  </div>
                  {emailStep === "email" && (
                    <button
                      type="button"
                      disabled={isEmailOtpSending || !emailValue}
                      className="flex btn-size items-center justify-center px-3 py-[5.5px] whitespace-nowrap bg-btnBgColor text-white disabled:opacity-30 disabled:cursor-not-allowed w-full sm:w-auto"
                      onClick={handleSendEmailOtp}
                    >
                      {isEmailOtpSending ? (
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                      ) : (
                        <img
                          src={requestOtpImg}
                          alt="request otp"
                          className="mr-2"
                        />
                      )}
                      <span>Request OTP</span>
                    </button>
                  )}
                </div>

                {emailStep === "emailOtp" && (
                  <div className="flex flex-col gap-2 mt-2">
                    <div className="text-allfonts text-fontColor">
                      Enter 6-digit OTP sent to your email:
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <OTPInput
                        length={6}
                        value={emailOtpValue}
                        onChange={setEmailOtpValue}
                        onSubmit={handleVerifyEmailOtp}
                        disabled={isEmailOtpVerifying}
                      />
                      <button
                        type="button"
                        disabled={
                          isEmailOtpVerifying || emailOtpValue.length !== 6
                        }
                        className="flex items-center justify-center px-3 py-[3.5px] bg-btnBgColor text-white btn-size disabled:cursor-not-allowed disabled:opacity-30 whitespace-nowrap"
                        onClick={handleVerifyEmailOtp}
                      >
                        <span>Verify OTP</span>
                        <img src={arrowImg} alt="arrow" className="ml-2" />
                      </button>
                    </div>
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          setEmailStep("email");
                          setEmailOtpValue("");
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm underline w-fit"
                      >
                        Change Email
                      </button>
                      <button
                        type="button"
                        onClick={handleResendEmailOtp}
                        disabled={emailResendTimer > 0 || isEmailOtpSending}
                        className="text-blue-600 hover:text-blue-800 text-sm underline w-fit disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {emailResendTimer > 0
                          ? `Resend OTP (${emailResendTimer}s)`
                          : "Resend OTP"}
                      </button>
                    </div>
                  </div>
                )}

                {emailStep === "emailVerified" && (
                  <div className="flex items-center gap-2 ">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15.275"
                      height="10.75"
                      viewBox="0 0 19.275 13.75"
                    >
                      <path
                        id="seen-svgrepo-com"
                        d="M2.421,12.833a1.382,1.382,0,0,1,1.953.033L8.8,17.451,18.993,7.381a1.382,1.382,0,0,1,1.934,1.965L9.749,20.4a1.382,1.382,0,0,1-.967.4H8.769a1.382,1.382,0,0,1-.978-.414l-5.4-5.6a1.382,1.382,0,0,1,.033-1.953Z"
                        transform="translate(-2 -7.047)"
                        fill="#099624"
                      />
                    </svg>

                    <span className="text-allfonts-small  text-fontColor  ">
                      Email Address verified successfully
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── 2. Phone Number ── */}
          <div
            className="flex items-start gap-3 sm:gap-4 border border-[#CCCCCC] rounded-xl bg-white p-3 sm:p-4 md:p-6 w-full md:w-[75%]"
            style={{ borderWidth: "0.25px" }}
          >
            <CountBox count={2} />
            <div className="flex-1 min-w-0">
              <SectionLabel label="Phone Number (Mobile)" required />
              <p className="text-allfonts-small italic mb-2 text-fontColor">
                * We will send a One-Time-PIN (OTP) to verify your Phone Number.
              </p>
              <p className="text-allfonts-small italic mb-3 text-fontColor">
                * Phone Number in this formate: +Country Code-Phone Number (eg:
                +91-9999999999)
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div
                    className="w-full max-w-[450px]"
                    ref={phoneInputWrapperRef}
                  >
                    <PhoneInput
                      country={"in"}
                      value={formik.values.phoneNumber}
                      onChange={(phone) =>
                        formik.setFieldValue("phoneNumber", phone)
                      }
                      disabled={otpStep === "otp" || otpStep === "verified"}
                      inputStyle={{
                        border: "0.25px solid #666666",
                        borderRadius: "4px",
                        fontSize: "16px",
                        width: "100%",
                        backgroundColor: "#F9FAFB",
                        paddingTop: "3.5px",
                        paddingBottom: "3.5px",
                        height: "auto",
                      }}
                      buttonStyle={{
                        border: "0.25px solid #666666",
                        borderRight: "none",
                        borderRadius: "4px 0 0 4px",
                        backgroundColor: "#F9FAFB",
                      }}
                      containerStyle={{ width: "100%" }}
                      placeholder="Country Code-Phone Number"
                    />
                  </div>
                  {otpStep === "phone" && (
                    <button
                      type="button"
                      disabled={
                        isOtpSending ||
                        !companyId ||
                        !isValidPhoneNumber(formik.values.phoneNumber)
                      }
                      className="flex btn-size items-center justify-center px-3 py-[5.5px] whitespace-nowrap bg-btnBgColor text-white disabled:opacity-30 disabled:cursor-not-allowed w-full sm:w-auto"
                      onClick={handleSendOtpClick}
                    >
                      {isOtpSending ? (
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                      ) : (
                        <img
                          src={requestOtpImg}
                          alt="request otp"
                          className="mr-2"
                        />
                      )}
                      <span>Request OTP</span>
                    </button>
                  )}
                </div>

                {otpStep === "otp" && (
                  <div className="flex flex-col gap-2 mt-2">
                    <div className="text-allfonts text-fontColor">
                      Enter 6-digit OTP sent to your phone:
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <OTPInput
                        length={6}
                        value={otpValue}
                        onChange={setOtpValue}
                        onSubmit={handleVerifyOtp}
                        disabled={isOtpVerifying}
                      />
                      <button
                        type="button"
                        disabled={isOtpVerifying || otpValue.length !== 6}
                        className="flex items-center justify-center px-3 py-[3.5px] bg-btnBgColor text-white btn-size disabled:cursor-not-allowed disabled:opacity-30 whitespace-nowrap"
                        onClick={handleVerifyOtp}
                      >
                        <span>Verify OTP</span>
                        <img src={arrowImg} alt="arrow" className="ml-2" />
                      </button>
                    </div>
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          setOtpStep("phone");
                          setOtpValue("");
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm underline w-fit"
                      >
                        Change Phone Number
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (phoneResendTimer > 0 || isOtpSending) return;
                          setIsOtpSending(true);
                          try {
                            await apiService.sendPhoneOtpV2(
                              formik.values.phoneNumber,
                              companyId,
                            );
                            showSnackbar(
                              "OTP resent to your phone number",
                              "success",
                            );
                            startPhoneResendTimer();
                          } catch (error) {
                            showSnackbar(error.message, "error");
                          } finally {
                            setIsOtpSending(false);
                          }
                        }}
                        disabled={phoneResendTimer > 0 || isOtpSending}
                        className="text-blue-600 hover:text-blue-800 text-sm underline w-fit disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {phoneResendTimer > 0
                          ? `Resend OTP (${phoneResendTimer}s)`
                          : "Resend OTP"}
                      </button>
                    </div>
                  </div>
                )}

                {otpStep === "verified" && (
                  <div className="flex items-center gap-2 ">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15.275"
                      height="10.75"
                      viewBox="0 0 19.275 13.75"
                    >
                      <path
                        id="seen-svgrepo-com"
                        d="M2.421,12.833a1.382,1.382,0,0,1,1.953.033L8.8,17.451,18.993,7.381a1.382,1.382,0,0,1,1.934,1.965L9.749,20.4a1.382,1.382,0,0,1-.967.4H8.769a1.382,1.382,0,0,1-.978-.414l-5.4-5.6a1.382,1.382,0,0,1,.033-1.953Z"
                        transform="translate(-2 -7.047)"
                        fill="#099624"
                      />
                    </svg>
                    <span className="text-allfonts-small  text-fontColor  ">
                      Phone number verified successfully
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Fields shown after both OTPs verified ── */}
          {bothVerified && (
            <>
              {/* ── 3. Resume Upload ── */}
              <div
                className="flex items-start gap-3 sm:gap-4 border border-[#CCCCCC] rounded-xl bg-white p-3 sm:p-4 md:p-6 w-full md:w-[75%]"
                style={{ borderWidth: "0.25px" }}
              >
                <CountBox count={3} />
                <div className="flex-1 min-w-0">
                  <SectionLabel label="Upload Resume" required />
                  <p className="text-allfonts-small italic mb-2 text-fontColor">
                    * Accepted file formats are: PDF, MS Word (Doc, Docx).
                  </p>
                  <p className="text-allfonts-small italic mb-3 text-fontColor">
                    * Max File Size: 5 MB
                  </p>
                  {!resumeFile ? (
                    <label className="flex items-center gap-3 cursor-pointer w-fit">
                      <div className="flex items-center justify-center px-3 py-[3.5px]  bg-btnBgColor text-white btn-size cursor-pointer">
                        <img src={uploadImg} alt="upload" className="mr-2" />
                        <span className="text-btnFontSize">Upload Resume</span>
                      </div>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleResumeUpload}
                        className="hidden"
                      />
                    </label>
                  ) : (
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-1">
                      <div className="flex items-center gap-2 border border-[#CCCCCC] rounded-[4px] px-3 py-[3px] max-w-full min-w-0">
                        <svg
                          className="w-4 h-4 text-fontColor shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <span className="text-allfonts text-fontColor max-w-[140px] sm:max-w-[200px] truncate">
                          {resumeFile.name}
                        </span>
                        <span className="text-allfonts-small text-gray-400 shrink-0">
                          ({(resumeFile.size / 1024).toFixed(0)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveResume}
                        className="flex text-btnFontSize items-center justify-center px-4 py-[5.5px] text-white bg-btnBgColor disabled:opacity-30 btn-size w-fit"
                      >
                        <img
                          src={removeImg}
                          alt="remove"
                          className="w-3 h-3 mr-2"
                        />{" "}
                        Remove
                      </button>
                    </div>
                  )}
                  {resumeError && (
                    <p className="text-red-500 text-allfonts-small mt-1">
                      {resumeError}
                    </p>
                  )}
                </div>
              </div>

              {/* ── DYNAMIC FIELDS FROM JSON ── */}
              {bothVerified &&
                sortedFields.map((field, index) => (
                  <div
                    id={`field-${field.id}`}
                    key={field.id}
                    className="flex items-start gap-3 sm:gap-4 border border-[#CCCCCC] rounded-xl bg-white p-3 sm:p-4 md:p-6 w-full md:w-[75%]"
                    style={{ borderWidth: "0.25px" }}
                  >
                    <CountBox count={index + 4} />
                    <div className="flex-1 min-w-0">
                      {field.type !== "checkbox" && (
                        <>
                          <p className="text-allfonts font-semibold tracking-wide text-fontColor mb-1">
                            {field.label}
                            {field.required && (
                              <span className="text-red-500 ml-0.5">*</span>
                            )}
                          </p>
                          {field.description && (
                            <p className="text-allfonts-small italic mb-3 text-fontColor">
                              {field.description}
                            </p>
                          )}
                        </>
                      )}
                      {renderField(field)}
                      {fieldErrors[field.id] && (
                        <p className="text-red-500 text-allfonts-small mt-2">
                          {fieldErrors[field.id]}
                        </p>
                      )}
                    </div>
                  </div>
                ))}

              {/* ── CONSENT CHECKBOX ── */}
              <div
                id="field-_consent"
                className="flex items-start gap-3 sm:gap-4 border border-[#CCCCCC] rounded-xl bg-white p-3 sm:p-4 md:p-6 w-full md:w-[75%]"
                style={{ borderWidth: "0.25px" }}
              >
                <div className="flex-1 min-w-0">
                  <label className="flex items-start space-x-2.5 text-allfonts cursor-pointer text-fontColor select-none">
                    <input
                      type="checkbox"
                      checked={consentGiven}
                      onChange={(e) => {
                        setConsentGiven(e.target.checked);
                        if (e.target.checked) {
                          setFieldErrors((prev) => ({ ...prev, _consent: null }));
                        }
                      }}
                      className="transform scale-125 shrink-0"
                      style={{ accentColor: "#002147",marginTop:".4rem" }}
                    />
                    <span>
                      By submitting this form, I consent that my personal
                      details can be stored.
                      <span className="text-red-500 ml-0.5">*</span>
                    </span>
                  </label>
                  {fieldErrors._consent && (
                    <p className="text-red-500 text-allfonts-small mt-2">
                      {fieldErrors._consent}
                    </p>
                  )}
                </div>
              </div>

              {/* ── CAPTCHA ── */}
              <div
                className="flex items-start gap-3 sm:gap-4 border border-[#CCCCCC] rounded-xl bg-white p-3 sm:p-4 md:p-6 w-full md:w-[75%]"
                style={{ borderWidth: "0.25px" }}
              >
                <div className="flex-1 min-w-0 overflow-x-auto">
                  <p className="text-allfonts text-fontColor mb-3">
                    {captchaMessage}
                  </p>
                  <Turnstile
                    key={turnstileKey}
                    siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                    onSuccess={(token) => {
                      setTurnstileToken(token);
                      setCaptchaMessage(
                        "Verification successful. Please click on Submit.",
                      );
                      setFieldErrors((prev) => ({ ...prev, _captcha: null }));
                    }}
                  />
                  {fieldErrors._captcha && (
                    <p className="text-red-500 text-allfonts-small mt-2">
                      {fieldErrors._captcha}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Fixed bottom action bar ── */}
      {bothVerified && (
        <div
          className="fixed bottom-0 left-0 w-full bg-[#F9FAFB] border-t border-[#cccccc] z-30 px-4 sm:px-6 py-2 flex items-center min-w-0"
          style={{ borderWidth: "0.25px" }}
        >
          <div className="text-[9px] sm:text-footerFontSize text-[#999999] break-words min-w-0 pr-2">
            ©{" "}
            {new Date().getFullYear() > 2026
              ? `2026-${new Date().getFullYear()}`
              : "2026"}
            , Needles and Haystacks Technology Pvt Ltd.
          </div>
          <div className="ml-auto shrink-0">
            <button
              type="button"
              disabled={isSubmitting || showLoader || !consentGiven}
              className="flex text-[11px] sm:text-btnFontSize items-center px-4 sm:px-6 py-1.5 text-white bg-btnBgColor btn-size disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              onClick={handleSubmit}
            >
              Submit
              <img
                src={arrowImg}
                alt="arrow"
                className="ml-2 w-[15px] h-[15px] sm:w-[16px] sm:h-[16px]"
              />
            </button>
          </div>
        </div>
      )}

      {/* ── Snackbar ── */}
      <Snackbar
        messages={snackbarMessages}
        onClose={handleCloseSnackbar}
        duration={6000}
        type={snackbarType}
      />

      {/* ── Footer ── */}
      {!bothVerified && <Footer bgColor="#F9FAFB" color="#999999" />}
    </div>
  );
}
